import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogActions, DialogContent, DialogTitle, TextField, Button, MenuItem, FormControl, InputLabel, Select, Typography, Grid, Box, Checkbox, List, ListItem, ListItemText, LinearProgress
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { v4 as uuidv4 } from 'uuid';
import { getFirestore, doc, getDoc, getDocs, updateDoc, addDoc, collection } from 'firebase/firestore';
import { app } from './auth/firebaseConfig';
import AttachFileIcon from "@mui/icons-material/AttachFile";
import Comments from './Comments';
import { fetchTenantUsers, fetchLastSerialNumber, updateLastSerialNumber, fetchAssignedGroupsByEmail } from '../DatabaseApis/DBQuery';
// import { storage } from './auth/firebaseConfig';
// import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { Divider } from "@mui/material";
import { getCookie } from '../utils/cookieUtils';
import { saveNewCard } from "../utils/firestoreCards";
import { getAuth } from "firebase/auth";
import { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import {storage} from './auth/firebaseConfig';

// Helper to format timestamps for logs
const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'Unknown Date';
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return 'Unknown Date';
  const formattedDate = date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
  const [datePart, timePart] = formattedDate.split(', ');
  return `${datePart.replace(/ /g, "/")}, ${timePart || ''}`;
};

const riskCategories = ["Financial", "Technical", "Operational", "Security", "Reputational"];
const likelihoodOptions = [
  "1-Very Low",
  "2-Low",
  "3-Medium",
  "4-High",
  "5-Very High"
];
const impactOptions = [
  "1-Very Low",
  "2-Low",
  "3-Medium",
  "4-High",
  "5-Very High"
];
const currentStateOptions = ["Identified", "Analyzed", "Mitigation Planned", "In Progress", "Monitoring", "Done", "Blocked", "Withdrawn"];
const MAX_FILE_COUNT = 10;
const MAX_FILE_SIZE_MB = 5;
// Drag and drop/file upload handlers must be inside the component to access state

const AddRiskCardDialog = ({ open, onClose, onSave, prjName, boardId, projectId, tenantId, userId, userName, initialData }) => {
  // Columns for status selection (same as AddCardDialog)
  const [columns, setColumns] = useState([]);
  // Drag and drop/file upload handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (uploadedFiles.length + files.length > MAX_FILE_COUNT) {
      alert(`Cannot upload ${files.length} files. You can only upload up to ${MAX_FILE_COUNT} files total. Currently ${uploadedFiles.length} files uploaded.`);
      return;
    }
    files.forEach(file => uploadFile(file));
  };

  const uploadFile = async (file) => {
    // Check if file size exceeds limit from environment variable
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      alert(`File size must be less than ${MAX_FILE_SIZE_MB}MB`);
      return;
    }

    // Check if maximum file count is reached
    if (uploadedFiles.length >= MAX_FILE_COUNT) {
      alert(`Maximum number of files (${MAX_FILE_COUNT}) reached. Please delete some files before uploading more.`);
      return;
    }

    try {
      // Generate unique file ID and sanitize filename
      const fileId = uuidv4();
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.]/g, '_');
      const filePath = `card-images/${fileId}_${sanitizedFileName}`;
      
      // Create reference to file in Firebase Storage
      const fileRef = ref(storage, filePath);
      
      // Upload file with progress tracking
      const uploadTask = uploadBytesResumable(fileRef, file);
      
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          // Update progress state
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(prev => ({
            ...prev,
            [fileId]: progress
          }));
        },
        (error) => {
          console.error('Upload error:', error);
          alert('Error uploading file. Please try again.');
        },
        async () => {
          // Check again before adding to state to prevent race conditions
          if (uploadedFiles.length >= MAX_FILE_COUNT) {
            alert(`Maximum number of files (${MAX_FILE_COUNT}) reached. The upload will be discarded.`);
            // Delete the uploaded file from storage since we can't add it
            await deleteObject(fileRef);
            return;
          }

          // Get download URL after successful upload
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          
          // Add file to local state
          setUploadedFiles(prev => {
            // One final check before adding to state
            if (prev.length >= MAX_FILE_COUNT) {
              alert(`Maximum number of files (${MAX_FILE_COUNT}) reached. The upload will be discarded.`);
              // Delete the uploaded file from storage since we can't add it
              deleteObject(fileRef);
              return prev;
            }
            return [...prev, {
              id: fileId,
              name: file.name,
              url: downloadURL,
              uploadedAt: new Date().toISOString()
            }];
          });
          
          // Clear progress for this file
          setUploadProgress(prev => {
            const newProgress = { ...prev };
            delete newProgress[fileId];
            return newProgress;
          });
        }
      );
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error uploading file. Please try again.');
    }
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    if (uploadedFiles.length + files.length > MAX_FILE_COUNT) {
      alert(`Cannot upload ${files.length} files. You can only upload up to ${MAX_FILE_COUNT} files total. Currently ${uploadedFiles.length} files uploaded.`);
      return;
    }
    files.forEach(file => uploadFile(file));
    event.target.value = '';
  };

  const handleDeleteFile = async (fileId) => {
    const confirmed = window.confirm('Are you sure you want to delete this file?');
    if (!confirmed) return;

    try {
      // Find the file in the uploadedFiles array to get the correct path
      const fileToDelete = uploadedFiles.find(f => f.id === fileId);
      if (!fileToDelete) return;

      // Extract the file path from the URL
      const url = new URL(fileToDelete.url);
      const filePath = decodeURIComponent(url.pathname.split('/o/')[1]);
      
      // Create reference to file in Firebase Storage
      const fileRef = ref(storage, filePath);
      
      // Delete the file from storage
      await deleteObject(fileRef);
      
      // Update local state by removing the deleted file
      setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
    } catch (error) {
      console.error("Error deleting file:", error);
      alert('Error deleting file. Please try again.');
    }
  };

  const handleBoardChange = (newBoardId) => {
  setSelectedBoard(newBoardId);
  // Refresh columns based on new board
  const fetchColumns = async () => {
    try {
      const db = getFirestore(app);
      const boardRef = doc(db, "boards", newBoardId);
      const boardSnap = await getDoc(boardRef);
      let cols = [];
      if (boardSnap.exists()) {
        const boardData = boardSnap.data();
        cols = Array.isArray(boardData.columns) ? boardData.columns : [];
        if (cols.length > 0 && typeof cols[0] === "string") {
          cols = cols.map((col, idx) => ({ id: `column-${idx + 1}`, name: col }));
        }
      }
      if (!cols || cols.length === 0) {
        cols = currentStateOptions.map((name, idx) => ({ id: `column-${idx + 1}`, name }));
      }
      setColumns(cols);
      if (!currentState && cols.length > 0) {
        setCurrentState(cols[0].name);
      }
    } catch (err) {
      console.error("Error fetching columns for board:", err);
    }
  };
  fetchColumns();
};

const [riskSummary, setRiskSummary] = useState(initialData?.summary || initialData?.templateData?.riskSummary || '');
const [description, setDescription] = useState(initialData?.description || initialData?.templateData?.description || '');
  const [riskCategory, setRiskCategory] = useState('');
  const [likelihood, setLikelihood] = useState('');
  const [impact, setImpact] = useState('');
  const [mitigationPlan, setMitigationPlan] = useState('');
  const [contingencyPlan, setContingencyPlan] = useState('');
  const [actionOwner, setActionOwner] = useState('');
  const [currentState, setCurrentState] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [comments, setComments] = useState([]);
  const [commentInput, setCommentInput] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedFilesToDelete, setSelectedFilesToDelete] = useState(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const [userList, setUserList] = useState([]);
  const [lastSerialNumber, setLastSerialNumber] = useState(0);
  const [taskNameError, setTaskNameError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingCard, setPendingCard] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({});
  const [editingCardId, setEditingCardId] = useState(null);
  const [boardList, setBoardList] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState(boardId || "");
  const [currentUserInfo, setCurrentUserInfo] = useState(null);
  const userData = getCookie('userData');
  const auth = getAuth();
  const email = auth.currentUser?.email;

  // Set current user info when component mounts or user data changes
  useEffect(() => {
    if (email && userData) {
      setCurrentUserInfo({
        uid: userData.userId || auth.currentUser?.uid,
        email: email,
        displayName: userData.userName || email.split('@')[0]
      });
    }
  }, [email, userData?.userId, userData?.userName]);

  // Calculate risk score from selected likelihood and impact
  const getRiskScore = () => {
    const parseValue = (val) => {
      const match = val.match(/^([1-5])/);
      return match ? parseInt(match[1], 10) : 1;
    };
    const likelihoodValue = parseValue(likelihood);
    const impactValue = parseValue(impact);
    return likelihoodValue * impactValue;
  };

  // Initialize form fields when dialog opens or initialData changes
  useEffect(() => {
    if (open && initialData) {
      // Set editing card ID
      setEditingCardId(initialData.id);
      
      // Initialize all form fields with existing data
      setRiskSummary(initialData.summary || initialData.templateData?.riskSummary || '');
      setDescription(initialData.description || initialData.templateData?.description || '');
      setRiskCategory(initialData.templateData?.riskCategory || '');
      setLikelihood(initialData.templateData?.likelihood || '');
      setImpact(initialData.templateData?.impact || '');
      setMitigationPlan(initialData.templateData?.mitigationPlan || '');
      setContingencyPlan(initialData.templateData?.contingencyPlan || '');
  setActionOwner(initialData.templateData?.actionOwner || initialData.assigneeName || '');
      setCurrentState(initialData.cardStatus || '');
      setDueDate(initialData.dueDate || '');
      setComments(initialData.templateData?.comments || []);
      setUploadedFiles(initialData.templateData?.attachments || []);
      
      // Set last serial number for existing cards
      if (initialData.cardId) {
        const serialNumber = Number(initialData.cardId.split('-').pop());
        setLastSerialNumber(serialNumber);
      }
    } else if (open && !initialData) {
      // Reset all fields for new card
      setEditingCardId(null);
      setRiskSummary('');
      setDescription('');
      setRiskCategory('');
      setLikelihood('');
      setImpact('');
      setMitigationPlan('');
      setContingencyPlan('');
      setActionOwner('');
      setCurrentState('');
      setDueDate('');
      setComments([]);
      setUploadedFiles([]);
      setLastSerialNumber(0);
    }
  }, [open, initialData]);

  useEffect(() => {
    // Fetch columns for the board (same as AddCardDialog)
    const fetchColumns = async () => {
      try {
        const db = getFirestore(app);
        const boardRef = doc(db, "boards", boardId);
        const boardSnap = await getDoc(boardRef);
        let cols = [];
        if (boardSnap.exists()) {
          const boardData = boardSnap.data();
          cols = Array.isArray(boardData.columns) ? boardData.columns : [];
          if (cols.length > 0 && typeof cols[0] === 'string') {
            cols = cols.map((col, idx) => ({ id: `column-${idx+1}`, name: col }));
          }
        }
        // Always set columns, fallback to currentStateOptions if empty
        if (!cols || cols.length === 0) {
          cols = currentStateOptions.map((name, idx) => ({ id: `column-${idx+1}`, name }));
        }
        setColumns(cols);
        if (!currentState && cols.length > 0) {
          setCurrentState(cols[0].name);
        }
      } catch (error) {
        setColumns(currentStateOptions.map((name, idx) => ({ id: `column-${idx+1}`, name })));
      }
    };
    if (open && boardId) fetchColumns();
    const fetchUsers = async () => {
      try {
        if (!tenantId) return;
        const users = await fetchTenantUsers(tenantId);
        setUserList(users);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    if (open && tenantId) fetchUsers();
  }, [open, boardId, tenantId]);

  useEffect(() => {
    if (open) {
      const getLastSerialNumber = async () => {
        try {
          const serialNumber = await fetchLastSerialNumber(projectId);
          setLastSerialNumber(serialNumber);
        } catch (error) {
          console.error("Failed to fetch serial number");
        }
      };
      getLastSerialNumber();
    }
  }, [open, projectId]);

  useEffect(() => {
  const fetchBoards = async () => {
    try {
      const db = getFirestore(app);
      const boardsRef = collection(db, "boards");
      const querySnapshot = await getDocs(boardsRef);
      const boards = [];
      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.projectId === projectId) {   // only boards under this project
          boards.push({ id: docSnap.id, name: data.boardName || "Untitled Board" });
        }
      });
      setBoardList(boards);
      // set default
      if (boardId) {
        setSelectedBoard(boardId);
      } else if (boards.length > 0) {
        setSelectedBoard(boards[0].id);
      }
    } catch (err) {
      console.error("Error fetching boards:", err);
    }
  };

  if (open && projectId) fetchBoards();
}, [open, projectId, boardId]);

// Helper to flush all fields
  const flushFields = () => {
    setRiskSummary("");
    setDescription("");
    setActionOwner("");
    setDueDate("");
    setRiskCategory("");
    setLikelihood("");
    setImpact("");
    setMitigationPlan("");
    setContingencyPlan("");
    setComments([]);
    setUploadedFiles([]);
    setTaskNameError(false);
  };

  const handleSave = async () => {
    if (!riskSummary.trim()) {
      setTaskNameError(true);
      return;
    } else {
      setTaskNameError(false);
    }

  const currentDate = new Date();
  const cardCreationDate = (initialData && !initialData.isDuplicate) ? initialData.cardCreationDate : currentDate.toISOString();
  const cardUpdatedOn = currentDate.toISOString();

  const parseValue = (val) => {
    const match = val.match(/^([1-5])/);
    return match ? parseInt(match[1], 10) : 1;
  };
  const likelihoodValue = parseValue(likelihood);
  const impactValue = parseValue(impact);
  const calculatedRiskScore = likelihoodValue * impactValue;

  const currentUserName = userData?.userName || currentUserInfo?.displayName || 'Unknown User';

      // Find assignee email from userList
    let assigneeEmail = '';
    if (Array.isArray(userList) && actionOwner) {
      const assigneeObj = userList.find(u => u.userName === actionOwner);
      if (assigneeObj && assigneeObj.email) assigneeEmail = assigneeObj.email;
    }

    // Get current user info for updated by
    const updatedByUserId = currentUserInfo?.uid || userId || '';
    const updatedByUserEmail = currentUserInfo?.email || email || '';

    // For created by, preserve original creator if editing
    let createdByUserId = updatedByUserId;
    let createdByUserEmail = updatedByUserEmail;
    if (editingCardId && initialData) {
      createdByUserId = initialData.cardCreatedByUserId || updatedByUserId;
      createdByUserEmail = initialData.cardCreatedByUserEmail || updatedByUserEmail;
    }

    console.log("User info:", { createdByUserId, createdByUserEmail, currentUserName });

  const cardPayload = {
    boardId: selectedBoard,
    projectId,
    tenantId,
    summary: riskSummary,
    description,
    cardStatus: currentState,
    dueDate,
  assigneeName: actionOwner,
  assigneeEmail: assigneeEmail,
    cardType: "Risk",
    cardCreatedByUserId: createdByUserId,
    cardCreatedByUserEmail: createdByUserEmail,
    cardCreationDate: cardCreationDate,
    cardCreationDateDisplay: cardCreationDate,
    cardUpdatedByUserId: updatedByUserId,
    cardUpdatedByUserEmail: updatedByUserEmail,
    cardUpdatedOn: cardUpdatedOn,
    templateData: {
      riskCategory,
      likelihood,
      impact,
      riskScore: calculatedRiskScore,
      mitigationPlan,
      contingencyPlan,
      currentState,
      comments,
      attachments: uploadedFiles,
    },
  };

  // Only add cardId for existing cards (updates), not for duplicates
  if (initialData && !initialData.isDuplicate) {
    cardPayload.cardId = initialData.cardId;
  }

  if (email) {
        setCurrentUserInfo({
          uid: userId,
          email: email
        });
      }

  try {
    if (editingCardId) {
      // Update existing card
      const db = getFirestore(app);
      const cardRef = doc(db, "cards", editingCardId);
      await updateDoc(cardRef, {
        ...cardPayload,
        cardUpdatedByUserId: userId,
        cardUpdatedOn: cardUpdatedOn,
        updatedAt: currentDate.toISOString(),
      });
      console.log("Risk card updated successfully");
    } else {
      // Add new card with Firestore transaction-safe counter
      const newCardId = await saveNewCard(projectId, prjName, cardPayload);
      cardPayload.cardId = newCardId;
      console.log("New risk card created successfully with ID:", newCardId);
    }
  } catch (err) {
    console.error("Error saving Risk card:", err);
    console.error("Error details:", {
      message: err.message,
      code: err.code,
      stack: err.stack
    });
    alert(`Failed to save risk card. Error: ${err.message}. Please try again.`);
    return;
  }

  // Flush fields after save
  setRiskSummary("");
  setDescription("");
  setActionOwner("");
  setDueDate("");
  setRiskCategory("");
  setLikelihood("");
  setImpact("");
  setMitigationPlan("");
  setContingencyPlan("");
  setComments([]);
  setUploadedFiles([]);

  // Close dialog - parent component will handle refreshing the data
  onClose();
};

  // Flush fields on cancel
  const handleCancel = () => {
    flushFields();
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={(event, reason) => {
        // Only allow closing via explicit close button, not backdrop click or escape key
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onClose();
        }
      }} 
      maxWidth="md" 
      fullWidth
      disableEscapeKeyDown
    >
      <Box sx={{ background: 'linear-gradient(45deg, #1e88e5 30%, #66bb6a 90%)', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>
        <DialogTitle>
          <Typography variant="h6" color="white">{(initialData && !initialData.isDuplicate) ? 'Edit Card' : 'Add Card'}</Typography>
        </DialogTitle>
      </Box>
      <DialogContent>
        <Grid container spacing={2}>
          {/* Card ID (non-editable) */}
          <Grid item xs={12}>
              <TextField
                margin="dense"
                label="Card ID"
                fullWidth
                variant="outlined"
                value={initialData?.cardId || (pendingCard && pendingCard.cardId ? pendingCard.cardId : '')}
                InputProps={{ readOnly: true, style: { backgroundColor: '#f5f5f5' } }}
                helperText="Card ID is auto-generated and cannot be edited"
              />
          </Grid>
          {/* ...existing fields... */}
          <Grid item xs={12}>
            <TextField autoFocus margin="dense" label="Summary" fullWidth variant="outlined" value={riskSummary} onChange={(e) => setRiskSummary(e.target.value.slice(0, 150))} error={taskNameError} helperText={taskNameError ? 'Risk Summary is required' : `${riskSummary.length}/150`} inputProps={{ maxLength: 150 }} />
          </Grid>
          <Grid item xs={12}>
            <TextField margin="dense" label="Description" fullWidth variant="outlined" multiline rows={4} value={description} onChange={(e) => setDescription(e.target.value.slice(0, 500))} helperText={`${description.length}/500`} inputProps={{ maxLength: 500 }} />
          </Grid>
          <Grid item xs={12}>
          <FormControl fullWidth variant="outlined" margin="dense">
          <InputLabel id="board-select-label">Select Board</InputLabel>
          <Select
           labelId="board-select-label"
           value={selectedBoard}
           onChange={(e) => handleBoardChange(e.target.value)}
           label="Select Board"
          >
          {boardList.map((board) => (
          <MenuItem key={board.id} value={board.id}>
          {board.name}
          </MenuItem>
          ))}
         </Select>
         </FormControl>
        </Grid>
            <Grid item xs={12}>
            <FormControl fullWidth variant="outlined" margin="dense">
              <InputLabel id="action-owner-label">Action Owner</InputLabel>
              <Select labelId="action-owner-label" value={actionOwner} onChange={(e) => setActionOwner(e.target.value)} label="Action Owner/Assignee">
                {[...userList].sort((a, b) => a.userName.localeCompare(b.userName)).map((user) => (<MenuItem key={user.id} value={user.userName}>{user.userName}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField margin="dense" label="Due Date" type="date" fullWidth variant="outlined" InputLabelProps={{ shrink: true }} value={dueDate} onChange={(e) => setDueDate(e.target.value)} inputProps={{ min: new Date().toISOString().split("T")[0] }} />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth variant="outlined" margin="dense">
              <InputLabel id="card-status-label">Card Status</InputLabel>
              <Select labelId="card-status-label" value={currentState} onChange={(e) => setCurrentState(e.target.value)} label="Card Status">
                {columns.map((col) => (
                  <MenuItem key={col.id || col.name} value={col.name}>{col.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Divider   sx={{
             my: 3, // margin top & bottom
             borderBottomWidth: 2,
             borderColor: "#4e54c8",
             borderRadius: 2,
             borderStyle: "dashed",
            }} 
            />
            <FormControl fullWidth variant="outlined" margin="dense">
              <InputLabel id="risk-category-label">Risk Category</InputLabel>
              <Select labelId="risk-category-label" value={riskCategory} onChange={(e) => setRiskCategory(e.target.value)} label="Risk Category">
                {riskCategories.map((cat) => (<MenuItem key={cat} value={cat}>{cat}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined" margin="dense">
              <InputLabel id="likelihood-label">Likelihood</InputLabel>
              <Select labelId="likelihood-label" value={likelihood} onChange={(e) => setLikelihood(e.target.value)} label="Likelihood">
                {likelihoodOptions.map((opt) => (<MenuItem key={opt} value={opt}>{opt}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined" margin="dense">
              <InputLabel id="impact-label">Impact</InputLabel>
              <Select labelId="impact-label" value={impact} onChange={(e) => setImpact(e.target.value)} label="Impact">
                {impactOptions.map((opt) => (<MenuItem key={opt} value={opt}>{opt}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
              <TextField
                margin="dense"
                label="Risk Score"
                fullWidth
                variant="outlined"
                value={getRiskScore()}
                disabled
                InputProps={{ style: { backgroundColor: '#f5f5f5' } }}
                helperText="Calculated from Likelihood x Impact"
              />
          </Grid>
          <Grid item xs={12}>
            <TextField margin="dense" label="Mitigation Plan" fullWidth variant="outlined" multiline rows={4} value={mitigationPlan} onChange={(e) => setMitigationPlan(e.target.value.slice(0, 500))} helperText={`${mitigationPlan.length}/500`} inputProps={{ maxLength: 500 }} />
          </Grid>
          <Grid item xs={12}>
            <TextField margin="dense" label="Contingency Plan" fullWidth variant="outlined" multiline rows={4} value={contingencyPlan} onChange={(e) => setContingencyPlan(e.target.value.slice(0, 500))} helperText={`${contingencyPlan.length}/500`} inputProps={{ maxLength: 500 }} />
          </Grid>
          {/* Attachments Section (drag-and-drop, same as AddCardDialog) */}
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
                <AttachFileIcon sx={{ mr: 1 }} />
                Attachments
              </Typography>
            </Box>

            {/* Drag and Drop Area */}
            <Box
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              sx={{
                border: `2px dashed ${isDragging ? '#4e54c8' : '#e0e0e0'}`,
                borderRadius: 1,
                p: 2,
                textAlign: 'center',
                bgcolor: isDragging ? 'rgba(78, 84, 200, 0.04)' : 'transparent',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: '#4e54c8',
                  bgcolor: 'rgba(78, 84, 200, 0.04)'
                }
              }}
              onClick={() => document.getElementById('risk-file-upload').click()}
            >
              <input
                type="file"
                id="risk-file-upload"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileUpload}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <AttachFileIcon sx={{ fontSize: 40, color: '#4e54c8' }} />
                <Typography variant="body2" color="textSecondary">
                  Drag and drop files here or click to upload
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {uploadedFiles.length}/{MAX_FILE_COUNT} files
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                  Max file size can be {MAX_FILE_SIZE_MB} mb.
                </Typography>
              </Box>
            </Box>
            {/* File List */}
            <List>
              {uploadedFiles.map((file, idx) => (
                <ListItem key={file.id || idx}>
                  <ListItemText 
                    primary={file.name} 
                    secondary={file.uploadedAt ? new Date(file.uploadedAt).toLocaleString() : ''}
                  />
                  <IconButton 
                    edge="end" 
                    onClick={() => file.id ? handleDeleteFile(file.id) : setUploadedFiles(prev => prev.filter((_, i) => i !== idx))}
                    sx={{ color: '#d32f2f' }}
                  >
                    <AttachFileIcon />
                  </IconButton>
                </ListItem>
              ))}
            </List>
            
            {/* Upload Progress */}
            {Object.keys(uploadProgress).length > 0 && (
              <Box sx={{ mt: 2 }}>
                {Object.entries(uploadProgress).map(([fileId, progress]) => (
                  <Box key={fileId} sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Uploading... {Math.round(progress)}%
                    </Typography>
                    <LinearProgress variant="determinate" value={progress} />
                  </Box>
                ))}
              </Box>
            )}
          </Grid>
            {/* Comments Section */}
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Comments
                  currentUserId={currentUserInfo?.uid}
                  currentUserEmail={currentUserInfo?.email}
                  existingComments={comments}
                  onCommentAdd={(comment) => {
                    const newComment = {
                      id: Date.now().toString(),
                      text: typeof comment === 'object' ? comment.text : comment,
                      authorId: currentUserInfo?.uid,
                      authorEmail: currentUserInfo?.email,
                      timestamp: new Date().toISOString(),
                      isEdited: false
                    };
                    setComments(prevComments => [...prevComments, newComment]);
                  }}
                  onCommentEdit={(commentId, updatedComment) => {
                    setComments(prevComments => prevComments.map(comment => {
                      if (comment.id === commentId) {
                        return {
                          ...comment,
                          text: updatedComment.text,
                          timestamp: updatedComment.timestamp,
                          isEdited: true,
                          editedAt: updatedComment.timestamp,
                          editedBy: {
                            id: currentUserInfo?.uid,
                            email: currentUserInfo?.email
                          }
                        };
                      }
                      return comment;
                    }));
                  }}
                  onCommentDelete={(commentId, shouldDeleteCompletely) => {
                    if (shouldDeleteCompletely) {
                      setComments(prevComments => prevComments.filter(comment => comment.id !== commentId));
                    } else {
                      setComments(prevComments => prevComments.map(comment => 
                        comment.id === commentId 
                          ? { ...comment, isDeleted: true }
                          : comment
                      ));
                    }
                  }}
                  isAuthenticated={!!currentUserInfo}
                />
              </Box>
            </Grid>
        </Grid>
      </DialogContent>
            <DialogActions sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-start',
              alignItems: 'center',
              bgcolor: '#e6f7fa',
              px: 2,
              py: 1,
              borderRadius: 2,
              fontFamily: 'Roboto, Arial, sans-serif',
              fontSize: '12px',
              m: 0
            }}>
              {initialData && (
                <Box sx={{
                  fontFamily: 'Roboto, Arial, sans-serif',
                  fontSize: '12px',
                  color: '#333',
                  p: 0,
                  m: 0,
                  minWidth: 220,
                  textAlign: 'left',
                  flex: '0 0 auto',
                  mr: 4
                }}>
                  <div>Created by: <span style={{ fontWeight: 500 }}>{initialData.cardCreatedByUserEmail || ''}</span></div>
                  <div>Created on: <span style={{ fontWeight: 500 }}>{formatTimestamp(initialData.cardCreationDate || initialData.createdAt)}</span></div>
                  <div>Last updated by: <span style={{ fontWeight: 500 }}>{initialData.cardUpdatedByUserEmail || ''}</span></div>
                  <div>Last updated on: <span style={{ fontWeight: 500 }}>{formatTimestamp(initialData.cardUpdatedOn || initialData.editedAt || initialData.updatedAt)}</span></div>
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', p: 0, m: 0, flex: '1 1 auto', justifyContent: 'flex-end' }}>
                <Button onClick={handleCancel} sx={{ fontFamily: 'Roboto, Arial, sans-serif', fontSize: '13px', color: '#1976d2', fontWeight: 500 }}>CANCEL</Button>
                <Button onClick={handleSave} color="primary" variant="contained" sx={{ fontFamily: 'Roboto, Arial, sans-serif', fontSize: '13px', fontWeight: 500 }}>SAVE</Button>
              </Box>
            </DialogActions>
    </Dialog>
  );
}

export default AddRiskCardDialog;